import type { LandingLayoutType } from 'ts/client/types';

/**
 * Where the gallery music control (`<sp-music>`) sits on the intro page. It's fixed to the
 * bottom-left corner everywhere else in the gallery, but each intro layout puts its own content
 * in different places, so the intro page moves it to wherever that layout leaves room:
 *
 * - `corner`: the usual bottom-left corner, which the layout leaves empty.
 * - `above-bottom-button`: small screens give the layout a full-width button along the bottom
 *   edge, so the control sits just above it.
 * - `top-left`: a bar of text and button runs along the bottom edge at every size, filling both
 *   bottom corners, so the control moves to the top-left corner.
 * - `inside-border` / `inside-outline` / `inside-lines`: the layout draws a frame near the edges,
 *   so the control sits just inside it rather than across it.
 * - `bottom-right-large`: on large screens the layout's content sits in the bottom-left corner.
 * - `bottom-right-small`: on small screens the layout's content sits in the bottom-left corner.
 *
 * The styles for each placement live in `client/gallery/css/components/sp-music.scss`.
 */
export type IntroMusicControlPlacement =
    | 'corner'
    | 'above-bottom-button'
    | 'top-left'
    | 'inside-border'
    | 'inside-outline'
    | 'inside-lines'
    | 'bottom-right-large'
    | 'bottom-right-small';

// Keyed by the layout `BaseLayout` renders for each type, so keep the two in step.
const PLACEMENT_BY_LAYOUT_TYPE: Record<LandingLayoutType, IntroMusicControlPlacement> = {
    'full-bottom-alt': 'top-left', // Foundation
    'full-bottom': 'top-left', // Limelight
    basic: 'above-bottom-button', // Accent
    'full-center': 'above-bottom-button', // Marquee
    hexagon: 'above-bottom-button', // Symmetry
    'no-cover': 'above-bottom-button', // Headline
    'detail-left': 'above-bottom-button', // Headline
    'border-thick': 'inside-border',
    'border-line': 'inside-outline',
    lines: 'inside-lines',
    'simple-left': 'bottom-right-large',
    // Small screens put Block Left's brand logo, and Block Right's text and button, bottom-left.
    'block-left': 'bottom-right-small',
    'block-right': 'bottom-right-small',
    split: 'corner', // Detail
    square: 'corner',
    'detail-right': 'corner',
    'block2-left': 'corner',
    'block2-right': 'corner',
    'box-left': 'corner',
    'box-right': 'corner',
    'simple-right': 'corner',
    banner: 'corner'
};

/**
 * Returns the music control's placement for the intro layout the gallery renders. Like
 * `BaseLayout`, a gallery with no cover photo, or a type it doesn't know, gets the Headline
 * layout's placement.
 */
export function getIntroMusicControlPlacement(
    landingLayoutType: LandingLayoutType,
    hasCoverPhoto: boolean
): IntroMusicControlPlacement {
    if (!hasCoverPhoto) {
        return PLACEMENT_BY_LAYOUT_TYPE['no-cover'];
    }

    return PLACEMENT_BY_LAYOUT_TYPE[landingLayoutType] ?? PLACEMENT_BY_LAYOUT_TYPE['no-cover'];
}
